/**
 * A single playable pad. Highlights differently depending on why it is lit:
 * during the demo the app is showing you what to play; during your turn it is
 * reacting to what you played.
 */

import React from 'react';
import { InstrumentPad } from '../../../data/africanInstruments';
import { DrumHitRating } from '../lib/scoring';

export interface DrumPadProps {
  pad: InstrumentPad;
  /** Lit because the demo is sounding this pad. */
  isPrompting: boolean;
  /** Lit because the player just struck it. */
  isStruck: boolean;
  /** Rating of the player's last strike on this pad, if any. */
  rating: DrumHitRating | null;
  disabled: boolean;
  onHit: () => void;
}

const RATING_RING: Record<DrumHitRating, string> = {
  PERFECT: 'border-sage-400 shadow-glow-sage',
  GOOD: 'border-amber-400 shadow-glow-amber',
  MISS: 'border-coral-400 shadow-glow-coral',
};

export const DrumPad: React.FC<DrumPadProps> = ({
  pad,
  isPrompting,
  isStruck,
  rating,
  disabled,
  onHit,
}) => {
  let state = 'bg-studio-750 border-studio-600 hover:border-amber-500/60 hover:bg-studio-700';

  if (isPrompting) {
    // The demo's colour is deliberately not amber, so "watch this" never looks
    // like "you hit it".
    state = 'bg-sage-400/25 border-sage-400 shadow-glow-sage scale-[0.98]';
  } else if (isStruck) {
    state = rating
      ? `bg-amber-500/25 ${RATING_RING[rating]} scale-[0.97]`
      : 'bg-amber-500 border-amber-400 shadow-glow-amber scale-[0.97]';
  }

  return (
    <button
      type="button"
      onPointerDown={(event) => {
        // Pointer-down rather than click: a rhythm game should register the
        // moment the finger lands, not when it lifts.
        event.preventDefault();
        if (!disabled) onHit();
      }}
      disabled={disabled}
      aria-label={`${pad.label} (key ${pad.keyBinding})`}
      // `touch-action: manipulation` is what actually stops double-tap zoom:
      // fast repeated taps on one pad are the whole game, and are exactly the
      // gesture the browser would otherwise read as "zoom in".
      style={{ touchAction: 'manipulation' }}
      className={`touch-target no-select no-tap-highlight flex flex-col items-center justify-center gap-1 rounded-xl border-2 px-3 py-4 transition-all duration-100 disabled:opacity-50 disabled:cursor-not-allowed ${state}`}
    >
      <span className="font-semibold text-stone-100 text-sm">{pad.label}</span>
      <span className="text-stone-400 text-xs">{pad.labelZh}</span>
      {/* There is no keyboard to press on a touch device, so the key chip is
          noise that also eats height inside an already-tight target. It stays
          in `aria-label` either way. */}
      <kbd className="hidden sm:inline-block mt-1 font-mono text-[10px] uppercase text-stone-500 bg-studio-950/60 rounded px-1.5 py-0.5">
        {pad.keyBinding}
      </kbd>
    </button>
  );
};
